import Image from 'next/image'
import capitalize from 'lodash/capitalize'
import { FC, SyntheticEvent } from 'react'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'

import generics from '@/utils/generics'
import { useStoreActions } from '@/store'
import { Agent, Location } from '@/types/agents'
import { getAgentTransactions } from '@/utils/agent-data'

import Icon from '../elements/Icon'

interface Props {
  testId?: string
  onClose?: () => void
  show: boolean
  agent: Agent
  location: Location
}

const AgentProfile: FC<Props> = ({ testId, onClose, show, agent, location }) => {
  const { onElementClickedTracking } = useStoreActions(
    actions => actions.tracking
  )

  const { onProductClickedTracking } = useStoreActions(
    actions => actions.tracking
  )

  if (!show) {
    return null
  }

  if (show) {
    document.getElementsByTagName('html')[0].style.overflow = 'hidden'
  }

  const tabClick = (index: number) => {
    const tabs = ['Highlights', 'Statistics', 'Specialties']

    const elementClickedPayload = generics.buildElementClickedPayload(
      'tab',
      'profile',
      '0',
      tabs[index],
      '',
      tabs[index]
    )
    onElementClickedTracking(elementClickedPayload)
  }

  const connectClick = (event: SyntheticEvent) => {
    const productClickedPayload = generics.buildProductClickedPayload(
      agent.id,
      agent.full_name || '',
      agent.details.specialties ? agent.details.specialties.join(',') : '',
      agent.id,
      'profile middle',
      0
    )
    onProductClickedTracking(productClickedPayload)
  }

  const { transactions, totalTransactions, localTransactions } = getAgentTransactions(agent)

  const showHighlightsTab =
    agent.details.achievements?.length > 0 || agent.details.description
  const showSpecialtiesTab =
    agent.details.specialties?.length > 0 || agent.details.languages?.length > 0

  return (
    <div
      id="agent-modal"
      className="Modal"
      data-modal
      role="dialog"
      aria-label="Agent modal"
      aria-hidden="true"
    >
      <div
        onClick={onClose}
        aria-hidden="true"
        className="Modal-overlay"
        data-target="agent-modal"
      ></div>
      <div
        className="Modal-container bg-white w-1/2x rounded-lg"
        role="document"
        aria-label="Agent modal content"
      >
        <div
          className="grid grid-cols-1 md:grid-cols-12 md:gap-8 rounded-lg w-full"
          style={{ overflow: 'auto' }}
        >
          <div className="md:col-span-4 bg-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-5 sm:gap-6 md:grid-cols-1 md:gap-0 p-6 text-center md:text-left">
              <div className="sm:col-span-2">
                <Image
                  src={agent.details.avatar_url}
                  width={200}
                  height={200}
                  loading="lazy"
                  alt="Author"
                  className="AgentProfile-picture rounded"
                />
              </div>
              <div className="sm:col-span-3">
                <div className="mt-6">
                  <h4 className="mt-4 md:mt-0 mb-2">
                    {agent.first_name} {agent.last_name}
                  </h4>
                  <p className="mb-0 text-gray-700">{agent.agency?.name || ''}</p>
                  {agent.details.experience_years && (
                    <p className="mb-0 text-gray-700">
                      {agent.details.experience_years} years in business
                    </p>
                  )}
                  <a
                    data-testid="connect"
                    onClick={connectClick}
                    href={agent.details.destination_url}
                    target="_blank"
                    className="Button Button--primary mt-6 hidden md:block"
                    rel="noreferrer"
                  >
                    Connect
                    <Icon
                      variant="arrow"
                      className="fill-white Icon--xs ml-1 mb-2"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-8 md:pr-8">
            <Tabs
              selectedTabClassName="is-active"
              onSelect={index => tabClick(index)}
              defaultIndex={showHighlightsTab ? 0 : 1}
            >
              <TabList
                className="Tabs pt-2 md:pt-6 md:mb-12 sticky bg-white z-5"
                style={{ top: 0 }}
              >
                <Tab
                  data-testid="tab-Highlights"
                  className="Tabs-item"
                  hidden={!showHighlightsTab}
                >
                  <div className="Tabs-link px-5">Highlights</div>
                </Tab>
                <Tab data-testid="tab-Statistics" className="Tabs-item">
                  <div className="Tabs-link px-5">Statistics</div>
                </Tab>
                <Tab
                  data-testid="tab-Specialties"
                  className="Tabs-item"
                  hidden={!showSpecialtiesTab}
                >
                  <div className="Tabs-link px-5">Specialties</div>
                </Tab>
              </TabList>
              <TabPanel className="Tabs-panel px-4 py-0">
                {agent.details.description && (
                  <>
                    <p className="subtitle type-heading-six uppercase">
                      <Icon
                        variant="user"
                        className="Icon--sm mr-3 icon-highlight-blue-600"
                      />
                      <span className="text-blue-600">About</span>
                    </p>
                    <p className="text-gray-700 mb-12">
                      {agent.details.description}
                    </p>
                  </>
                )}
                {agent.details.achievements?.length > 0 && (
                  <>
                    <p className="subtitle type-heading-six uppercase">
                      <Icon
                        variant="ribbon"
                        className="Icon--sm mr-3 icon-highlight-blue-600"
                      />
                      <span className="text-blue-600">Achievements</span>
                    </p>
                    <p className="text-gray-900 font-bold mb-2">
                      HomeLight Achievements 2021 Awards Winner
                    </p>
                    <Image
                      src="https://a.storyblok.com/f/102997/408x80/831a936c0d/homelight-logo.png"
                      width={102}
                      height={20}
                      loading="lazy"
                      className="mb-2"
                      alt="Homelight logo"
                    />
                    <ul className="text-blue-600">
                      {agent.details.achievements?.map(achievement => (
                        <li className="mb-6 pt-2" key={achievement.title}>
                          <p className="text-gray-700 text-sm mb-0">
                            <span className="font-bold block">
                              {achievement.title}
                            </span>
                            {achievement.description}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {/* <p className="subtitle text-blue-600 type-heading-six uppercase">
                  Credentials
                </p>
                <ul className="text-blue-600">
                  <li className="mb-6 pt-2">
                    <p className="text-gray-700 text-sm mb-4">
                      <span className="font-bold block">
                        Top producer
                      </span>
                      Real estate agents earn this achievement when they rank in
                      the top 5% of agents in their area based on total sales
                      volume.
                    </p>
                    <Image
                      width={90}
                      height={60}
                      src="https://picsum.photos/90/60"
                      alt="Logo placeholder"
                    />
                  </li>
                  <li className="mb-6 pt-2">
                    <p className="text-gray-700 text-sm mb-4">
                      <span className="font-bold block">
                        Sells homes fast
                      </span>
                      Real estate agents earn this achievement when they sell
                      their clients homes faster than the average days on
                      market. It is granted to the top 5% of agents in the area
                      based on average days on market for seller side
                      transactions.
                    </p>
                    <Image
                      width={90}
                      height={60}
                      src="https://picsum.photos/90/60"
                      alt="Logo placeholder"
                    />
                  </li>
                  <li className="mb-6 pt-2">
                    <p className="text-gray-700 text-sm mb-4">
                      <span className="font-bold block">
                        Top negotiator
                      </span>
                      Real estate agents earn this achievement when they sell
                      their clients homes faster than the average days on
                      market. It is granted to the top 5% of agents in the area
                      based on average days on market for seller side
                      transactions.
                    </p>
                    <Image
                      width={90}
                      height={60}
                      src="https://picsum.photos/90/60"
                      alt="Logo placeholder"
                    />
                  </li>
                </ul> */}
              </TabPanel>
              <TabPanel className="Tabs-panel px-4 py-0">
                <p className="subtitle type-heading-six uppercase">
                  <Icon
                    variant="mortgageMarket"
                    className="Icon--sm mr-3 icon-highlight-blue-600"
                  />
                  <span className="text-blue-600">Statistics</span>
                </p>
                <div className="grid gap-6 grid-cols-4">
                  <div className="col-span-3">
                    <p className="font-bold mb-4 text-gray-900">
                      Closings in the last 12 months
                    </p>
                    <ul className="text-blue-600 text-sm">
                      <li className="mb-2">
                        <span className="text-gray-700">
                          Single-family homes: {transactions['single_family']}
                        </span>
                      </li>
                      <li className="mb-2">
                        <span className="text-gray-700">
                          Condos: {transactions['condominium']}
                        </span>
                      </li>
                      <li className="mb-2">
                        <span className="text-gray-700">
                          Townhomes: {transactions['townhome']}
                        </span>
                      </li>
                      <li className="mb-2">
                        <span className="text-gray-700">
                          Total closings in {location.city}, {location.state} area: {localTransactions}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-bold mb-2 text-right">
                      {totalTransactions}
                    </p>
                  </div>
                </div>
                <div className="grid gap-6 grid-cols-4">
                  <div className="col-span-3">
                    <p className="font-bold mb-2 text-gray-900">
                      Average amount of days on market
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                      Average number of days this agent&#39;s home sale listings
                      stay on the market
                    </p>
                  </div>
                  <div>
                    <p className="font-bold mb-2 text-right">
                      {agent.details.avg_days_on_market} days
                    </p>
                  </div>
                </div>
                <div className="grid gap-6 grid-cols-4">
                  <div className="col-span-3">
                    <p className="font-bold mb-2 text-gray-900">
                      Average sold-to-list price ratio
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                      The final sale price (what a buyer pays for a home)
                      divided by the last list price expressed as a percentage.
                    </p>
                  </div>
                  <div>
                    <p className="font-bold mb-2 text-right">
                      {Math.round(agent.details.avg_sell_to_list_ratio * 100) ||
                        '--'}
                      %
                    </p>
                  </div>
                </div>
              </TabPanel>
              <TabPanel className="Tabs-panel px-4 py-0">
                {agent.details.specialties?.length > 0 && (
                  <>
                    <p className="subtitle type-heading-six uppercase">
                      <Icon
                        variant="mortgageReviews"
                        className="Icon--sm mr-3 icon-highlight-blue-600"
                      />
                      <span className="text-blue-600">Specialties</span>
                    </p>
                    <ul className="text-gray-900">
                      {agent.details.specialties?.map(specialtie => (
                        <li key={specialtie}>{capitalize(specialtie)}</li>
                      ))}
                    </ul>
                  </>
                )}

                {agent.details.languages?.length > 0 && (
                  <>
                    <p className="subtitle type-heading-six uppercase">
                      <Icon
                        variant="globeWorld"
                        className="Icon--sm mr-3 icon-highlight-blue-600"
                      />
                      <span className="text-blue-600">Additional languages</span>
                    </p>
                    <ul className="text-gray-900">
                      {agent.details.languages?.map(language => (
                        <li key={language}>{capitalize(language)}</li>
                      ))}
                    </ul>
                  </>
                )}
              </TabPanel>
            </Tabs>
            <div
              className="sticky p-4 box-shadow-top bg-white md:hidden"
              style={{ bottom: 0 }}
            >
              <a
                data-testid="connect-mobile"
                onClick={connectClick}
                href={agent.details.destination_url}
                target="_blank"
                className="Button Button--primary block"
                rel="noreferrer"
              >
                Connect
                <Icon
                  variant="arrow"
                  className="fill-white Icon--xs ml-1 mb-2"
                />
              </a>
            </div>
          </div>
        </div>
        <button
          data-testid={testId}
          className="Modal-close fill-gray-900 border-none shadow-none hover:fill-white top-7 right-7"
          onClick={onClose}
          type="button"
          aria-label="Close test modal"
          data-target="agent-modal"
        >
          <span className="sr-only">Close button</span>
        </button>
      </div>
    </div>
  )
}

export default AgentProfile
